/*
 * Ported from the IDV proof of concept (frontend/src/utils/cameraControls.ts).
 *
 * Mechanical TypeScript -> JavaScript conversion (tsc --target ES2020, types
 * erased). The algorithms, thresholds and geometry are unchanged: this file is
 * pinned by src/utils/documentCrop.test.js and by the IDV backend contract.
 */

/* global globalThis */
/* `globalThis` is ES2020 and available in every browser this app targets;
   CRA 5's eslint env predates it, so it is declared here rather than
   changing the shared lint config or the ported logic. */
const FOCUS_SETTLE_MS = 700;
const FOCUS_DISTANCE_BRACKET_SETTLE_MS = 350;
const FOCUS_DISTANCE_INITIAL_VALUE = 0.2;
const PRACTICAL_FOCUS_DISTANCE_MAX = 5;
const ABSURD_FOCUS_DISTANCE_MAX = 100;
const FOCUS_DISTANCE_CANDIDATES = [
    0, 0.02, 0.05, 0.1, 0.2, 0.35, 0.5, 0.75, 1, 1.5, 2, 3, 5,
];
// Multiplier applied to the camera's minimum zoom for the document scan flow so
// the document fills more of the frame. Scaled from the minimum to stay correct
// whether the device reports zoom as 1x-based or 100-based, and clamped to max.
const DOCUMENT_DEFAULT_ZOOM_FACTOR = 1;
export async function applyBestDocumentCameraConstraints(track) {
    const capabilities = getTrackCapabilities(track);
    const settings = getTrackSettings(track);
    const warnings = [];
    const advanced = [];
    if (capabilities.focusMode?.includes('continuous')) {
        advanced.push({ focusMode: 'continuous' });
    }
    else if (capabilities.focusMode?.includes('manual')) {
        const manualFocusConstraint = { focusMode: 'manual' };
        if (capabilities.focusDistance) {
            manualFocusConstraint.focusDistance = clampFocusDistance(FOCUS_DISTANCE_INITIAL_VALUE, capabilities.focusDistance);
        }
        advanced.push(manualFocusConstraint);
    }
    if (capabilities.exposureMode?.includes('continuous')) {
        advanced.push({ exposureMode: 'continuous' });
    }
    if (capabilities.whiteBalanceMode?.includes('continuous')) {
        advanced.push({ whiteBalanceMode: 'continuous' });
    }
    if (capabilities.zoom && capabilities.zoom.max > capabilities.zoom.min) {
        const zoomBase = capabilities.zoom.min > 0 ? capabilities.zoom.min : 1;
        const defaultZoom = clamp(zoomBase * DOCUMENT_DEFAULT_ZOOM_FACTOR, capabilities.zoom.min, capabilities.zoom.max);
        advanced.push({ zoom: defaultZoom });
    }
    if (advanced.length === 0) {
        warnings.push('document_camera_constraints_unsupported');
        return {
            applied: false,
            warnings,
            capabilities: serializeTrackData(capabilities),
            settings: serializeTrackData(settings),
        };
    }
    const applied = await applyAdvancedConstraints(track, advanced, warnings);
    const nextSettings = getTrackSettings(track);
    if (typeof nextSettings.zoom === 'number' && nextSettings.zoom > 1.05) {
        warnings.push('digital_zoom_may_reduce_ocr_readability');
    }
    return {
        applied,
        warnings,
        capabilities: serializeTrackData(capabilities),
        settings: serializeTrackData(nextSettings),
    };
}
export async function tryRefocusCamera(track, point) {
    const capabilities = getTrackCapabilities(track);
    const warnings = [];
    let applied = false;
    let usedPointsOfInterest = false;
    let usedSingleShot = false;
    let usedContinuous = false;
    if (point && capabilities.pointsOfInterest !== undefined) {
        for (const candidate of pointOfInterestCandidates(point, capabilities)) {
            const pointApplied = await applyAdvancedConstraint(track, candidate, warnings);
            if (pointApplied) {
                applied = true;
                usedPointsOfInterest = true;
                break;
            }
        }
    }
    else if (point) {
        warnings.push('points_of_interest_not_exposed');
    }
    if (!applied &&
        capabilities.focusDistance &&
        capabilities.focusMode?.includes('manual')) {
        return {
            applied: false,
            usedPointsOfInterest: false,
            usedSingleShot: false,
            usedContinuous: false,
            requiresFocusDistanceBracket: true,
            warnings: [
                ...warnings,
                'manual_focus_distance_available',
                'tap_focus_fallback_to_focus_bracket',
            ],
        };
    }
    if (!applied && capabilities.focusMode?.includes('single-shot')) {
        applied = await applyAdvancedConstraint(track, { focusMode: 'single-shot' }, warnings);
        usedSingleShot = applied;
    }
    if (!applied && capabilities.focusMode?.includes('continuous')) {
        applied = await applyAdvancedConstraint(track, { focusMode: 'continuous' }, warnings);
        usedContinuous = applied;
    }
    const imageCaptureResult = await tryImageCaptureFocus(track);
    applied = imageCaptureResult.applied || applied;
    warnings.push(...imageCaptureResult.warnings);
    if (!applied && !supportsAnyFocusControl(capabilities)) {
        warnings.push('focus_controls_unsupported');
    }
    if (applied) {
        await wait(FOCUS_SETTLE_MS);
    }
    return {
        applied,
        usedPointsOfInterest,
        usedSingleShot,
        usedContinuous,
        requiresFocusDistanceBracket: false,
        warnings,
    };
}
export async function runFocusDistanceBracket(track, scoreFrame) {
    const capabilities = getTrackCapabilities(track);
    const attempts = [];
    if (!capabilities.focusMode?.includes('manual') || !capabilities.focusDistance) {
        return {
            applied: false,
            bestFocusDistance: null,
            bestSharpness: null,
            attempts,
            warnings: ['manual_focus_distance_unsupported'],
        };
    }
    const candidates = focusDistanceCandidates(capabilities.focusDistance);
    let bestAttempt = null;
    for (const focusDistance of candidates) {
        const attemptWarnings = [];
        const constraintApplied = await applyAdvancedConstraint(track, {
            focusMode: 'manual',
            focusDistance,
        }, attemptWarnings);
        await wait(FOCUS_DISTANCE_BRACKET_SETTLE_MS);
        const score = constraintApplied ? scoreFrame() : null;
        const attempt = {
            focusDistance,
            sharpness: score?.sharpness ?? null,
            brightness: score?.brightness ?? null,
            contrast: score?.contrast ?? null,
            passed: score?.passed ?? false,
            warnings: score ? [...attemptWarnings, ...score.warnings] : attemptWarnings,
        };
        if (!score) {
            attempt.warnings.push('score_unavailable');
        }
        attempts.push(attempt);
        if (!score || score.brightness < 20 || score.contrast < 5)
            continue;
        if (!bestAttempt || score.sharpness > (bestAttempt.sharpness ?? -1)) {
            bestAttempt = attempt;
        }
    }
    if (!bestAttempt) {
        return {
            applied: false,
            bestFocusDistance: null,
            bestSharpness: null,
            attempts,
            warnings: ['focus_bracket_no_valid_score'],
        };
    }
    const warnings = [];
    const applied = await applyAdvancedConstraint(track, {
        focusMode: 'manual',
        focusDistance: bestAttempt.focusDistance,
    }, warnings);
    return {
        applied,
        bestFocusDistance: applied ? bestAttempt.focusDistance : null,
        bestSharpness: applied ? bestAttempt.sharpness : null,
        attempts,
        warnings,
    };
}
export async function captureNativePhoto(track) {
    const warnings = [];
    const ImageCaptureConstructorValue = getImageCaptureConstructor();
    if (!ImageCaptureConstructorValue) {
        return {
            supported: false,
            blob: null,
            width: null,
            height: null,
            mimeType: null,
            warnings: ['image_capture_not_supported'],
        };
    }
    try {
        const imageCapture = new ImageCaptureConstructorValue(track);
        if (!imageCapture.takePhoto) {
            return {
                supported: false,
                blob: null,
                width: null,
                height: null,
                mimeType: null,
                warnings: ['take_photo_not_supported'],
            };
        }
        const blob = await imageCapture.takePhoto();
        const dimensions = await blobDimensions(blob);
        return {
            supported: true,
            blob,
            width: dimensions.width,
            height: dimensions.height,
            mimeType: blob.type || null,
            warnings,
        };
    }
    catch (error) {
        return {
            supported: true,
            blob: null,
            width: null,
            height: null,
            mimeType: null,
            warnings: [
                error instanceof Error
                    ? `take_photo_failed:${error.message}`
                    : 'take_photo_failed',
            ],
        };
    }
}
export async function getNativePhotoCapabilities(track) {
    const ImageCaptureConstructorValue = getImageCaptureConstructor();
    if (!ImageCaptureConstructorValue)
        return null;
    try {
        const imageCapture = new ImageCaptureConstructorValue(track);
        const capabilities = await imageCapture.getPhotoCapabilities?.();
        return serializeTrackData(capabilities);
    }
    catch {
        return null;
    }
}
export async function stopMediaStream(stream) {
    stream?.getTracks().forEach((track) => track.stop());
    await wait(400);
}
export async function trySetTorch(track, enabled) {
    const warnings = [];
    const capabilities = getTrackCapabilities(track);
    if (!capabilities.torch) {
        return { applied: false, warnings: ['torch_not_supported'] };
    }
    return {
        applied: await applyAdvancedConstraint(track, { torch: enabled }, warnings),
        warnings,
    };
}
export async function trySetZoom(track, zoom) {
    const warnings = [];
    const capabilities = getTrackCapabilities(track);
    if (!capabilities.zoom) {
        return { applied: false, warnings: ['zoom_not_supported'] };
    }
    if (!Number.isFinite(zoom)) {
        return { applied: false, warnings: ['zoom_invalid'] };
    }
    const min = Number.isFinite(capabilities.zoom.min)
        ? capabilities.zoom.min
        : 1;
    const max = Number.isFinite(capabilities.zoom.max)
        ? capabilities.zoom.max
        : min;
    const safeZoom = clamp(zoom, min, max);
    if (safeZoom !== zoom) {
        warnings.push('zoom_clamped_to_capabilities');
    }
    return {
        applied: await applyAdvancedConstraint(track, { zoom: safeZoom }, warnings),
        warnings,
    };
}
export function getTrackCapabilities(track) {
    return (track.getCapabilities?.() ?? {});
}
export function getTrackSettings(track) {
    return track.getSettings();
}
async function applyAdvancedConstraint(track, constraint, warnings) {
    return applyAdvancedConstraints(track, [constraint], warnings);
}
async function applyAdvancedConstraints(track, constraints, warnings) {
    try {
        await track.applyConstraints({
            advanced: constraints,
        });
        return true;
    }
    catch (error) {
        warnings.push(error instanceof Error
            ? `constraint_failed:${error.message}`
            : 'constraint_failed');
        return false;
    }
}
function pointOfInterestCandidates(point, capabilities) {
    const focusMode = capabilities.focusMode?.includes('single-shot')
        ? 'single-shot'
        : capabilities.focusMode?.includes('continuous')
            ? 'continuous'
            : undefined;
    const pointOnly = { x: point.x, y: point.y };
    const pointArea = {
        x: point.x,
        y: point.y,
        width: 0.12,
        height: 0.12,
    };
    const candidates = [
        {
            ...(focusMode ? { focusMode } : {}),
            pointsOfInterest: [pointOnly],
        },
        {
            ...(focusMode ? { focusMode } : {}),
            pointsOfInterest: [pointArea],
        },
        {
            pointsOfInterest: pointOnly,
        },
    ];
    if (capabilities.pointsOfInterest === undefined) {
        candidates.push({
            ...(focusMode ? { focusMode } : {}),
            pointsOfInterest: [pointOnly],
        });
    }
    return candidates;
}
function supportsAnyFocusControl(capabilities) {
    return Boolean(capabilities.pointsOfInterest !== undefined ||
        capabilities.focusMode?.includes('single-shot') ||
        capabilities.focusMode?.includes('continuous') ||
        (capabilities.focusMode?.includes('manual') && capabilities.focusDistance));
}
async function tryImageCaptureFocus(track) {
    const warnings = [];
    const ImageCaptureConstructorValue = getImageCaptureConstructor();
    if (!ImageCaptureConstructorValue) {
        return { applied: false, warnings };
    }
    try {
        const imageCapture = new ImageCaptureConstructorValue(track);
        const capabilities = await imageCapture.getPhotoCapabilities?.();
        if (capabilities?.focusMode?.includes('continuous') &&
            imageCapture.setOptions) {
            await imageCapture.setOptions({ focusMode: 'continuous' });
            return { applied: true, warnings };
        }
    }
    catch (error) {
        warnings.push(error instanceof Error
            ? `image_capture_focus_failed:${error.message}`
            : 'image_capture_focus_failed');
    }
    return { applied: false, warnings };
}
function getImageCaptureConstructor() {
    return globalThis.ImageCapture;
}
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
function clampFocusDistance(value, range) {
    return clamp(value, range.min, practicalFocusDistanceMax(range));
}
function focusDistanceCandidates(range) {
    const max = practicalFocusDistanceMax(range);
    const roundedCandidates = FOCUS_DISTANCE_CANDIDATES.map((candidate) => roundFocusDistance(clamp(candidate, range.min, max)));
    return [...new Set(roundedCandidates)];
}
function practicalFocusDistanceMax(range) {
    if (!Number.isFinite(range.max) || range.max > ABSURD_FOCUS_DISTANCE_MAX) {
        return Math.max(range.min, PRACTICAL_FOCUS_DISTANCE_MAX);
    }
    return Math.max(range.min, range.max);
}
function roundFocusDistance(value) {
    return Math.round(value * 1000) / 1000;
}
function wait(ms) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}
function serializeTrackData(value) {
    if (!value || typeof value !== 'object')
        return null;
    try {
        return JSON.parse(JSON.stringify(value));
    }
    catch {
        return { ...value };
    }
}
async function blobDimensions(blob) {
    try {
        if ('createImageBitmap' in globalThis) {
            const bitmap = await createImageBitmap(blob);
            const dimensions = {
                width: bitmap.width || null,
                height: bitmap.height || null,
            };
            bitmap.close();
            return dimensions;
        }
        const image = await loadImage(blob);
        return {
            width: image.naturalWidth || null,
            height: image.naturalHeight || null,
        };
    }
    catch {
        return {
            width: null,
            height: null,
        };
    }
}
function loadImage(blob) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        const objectUrl = URL.createObjectURL(blob);
        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('image_decode_failed'));
        };
        image.src = objectUrl;
    });
}
